import { useState } from "react";
import { toast } from "sonner";
import { Scale, PackageCheck, Truck, AlertTriangle, BadgeCheck, Pencil } from "lucide-react";
import { api, errCode } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useMaster } from "../lib/master";
import { kg } from "../lib/time";
import { ClassBadge } from "./Badges";
import { CameraCapture } from "./CameraCapture";
import { Inp, Sel } from "./Common";

export const useAct = (setBin) => {
  const { t, te } = useLang();
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true);
    try { const { data } = await fn(); setBin((b) => ({ ...b, ...data })); toast.success(t("saved")); return true; }
    catch (e) { toast.error(te(errCode(e))); return false; }
    finally { setBusy(false); }
  };
  return { run, busy };
};

const Diff = ({ d }) => d?.diff_kg === undefined ? null : (
  <p className="font-mono text-sm" data-testid="weight-diff">{d.diff_kg > 0 ? "+" : ""}{d.diff_kg} kg · {d.diff_pct > 0 ? "+" : ""}{d.diff_pct}%</p>
);

export const WeightPanel = ({ bin, setBin, http = api }) => {
  const { t } = useLang();
  const { run, busy } = useAct(setBin);
  const [method, setMethod] = useState("");
  const [val, setVal] = useState("");
  const fw = bin.field_weight;
  const save = () => run(() => http.post(`/bins/${bin.id}/weight`, { method, kg: method === "unable" ? null : Number(val) }));
  return (
    <section className="card p-4 space-y-3" data-testid="weight-panel">
      <div className="flex items-center justify-between"><h2 className="font-bold text-lg flex gap-2 items-center"><Scale size={20} />{t("field_weight")}</h2>
        <span className="font-mono text-sm font-bold bg-slate-100 px-2 py-1 rounded">{t("target")}: 120 kg</span></div>
      {fw && <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 space-y-1" data-testid="field-weight-result">
        <p className="font-bold">{t(`method_${fw.method}`)} · {kg(fw.kg)}</p><Diff d={fw} /><ClassBadge value={fw.classification} /></div>}
      {["active", "ready"].includes(bin.status) && <>
        <div className="grid gap-2">
          {["scale", "estimate", "unable"].map((m) => (
            <button key={m} type="button" onClick={() => setMethod(m)} data-testid={`weight-method-${m}`}
              className={`min-h-[52px] rounded-xl border-2 font-bold text-left px-4 transition-colors ${method === m ? "border-[#C8102E] bg-rose-50 text-[#800B28]" : "border-slate-300 bg-white"}`}>{t(`method_${m}`)}</button>
          ))}
        </div>
        {method && method !== "unable" && <Inp label={t("weight_kg")} type="number" inputMode="decimal" step="0.1" value={val} onChange={(e) => setVal(e.target.value)} testid="weight-kg-input" />}
        <button className="btn-secondary w-full" disabled={busy || !method || (method !== "unable" && !val)} onClick={save} data-testid="save-weight-button">{t("save_weight")}</button>
      </>}
    </section>
  );
};

export const PhotoPanel = ({ bin, setBin, onUploaded, http = api }) => {
  const { t } = useLang();
  const { run, busy } = useAct(setBin);
  const upload = async (blob, source) => {
    const fd = new FormData();
    fd.append("file", blob, "photo.jpg");
    fd.append("source", source);
    if (await run(() => http.post(`/bins/${bin.id}/photo`, fd))) onUploaded();
  };
  return (
    <section className="card p-4 space-y-3" data-testid="photo-panel">
      <h2 className="font-bold text-lg">{t("photo")}</h2>
      <CameraCapture onConfirm={upload} busy={busy} />
    </section>
  );
};

export const FieldActions = ({ bin, setBin, http = api }) => {
  const { t } = useLang();
  const { run, busy } = useAct(setBin);
  const canReady = bin.photo && bin.field_weight;
  if (bin.status === "active") return (
    <div className="space-y-1">
      <button className="btn-primary w-full" disabled={busy || !canReady} onClick={() => run(() => http.post(`/bins/${bin.id}/ready`))} data-testid="mark-ready-button"><PackageCheck size={20} />{t("mark_ready")}</button>
      {!canReady && <p className="text-xs text-center text-slate-600">{t("ready_requires")}</p>}
    </div>
  );
  if (bin.status === "ready") return <button className="btn-primary w-full" disabled={busy} onClick={() => run(() => http.post(`/bins/${bin.id}/sent`))} data-testid="mark-sent-button"><Truck size={20} />{t("mark_sent")}</button>;
  return null;
};

export const DockSummary = ({ d }) => {
  const { t } = useLang();
  return (
    <section className="card p-4 space-y-1" data-testid="dock-result">
      <p className="eyebrow">{t("dock")}</p>
      <p className="font-mono">{t("gross")}: {d.gross_kg} · {t("tare")}: {d.tare_kg} · <b data-testid="dock-net-value">{t("net")}: {kg(d.net_kg)}</b></p>
      <Diff d={d} /><ClassBadge value={d.classification} />
      <p className="text-xs text-slate-500">{d.weigh_count} {t("weigh_count")} · {d.by}</p>
    </section>
  );
};

export const PhSummary = ({ r }) => {
  const { t } = useLang();
  return (
    <section className="card p-4 space-y-1 border-l-4 border-teal-600" data-testid="ph-result">
      <p className="eyebrow">{t("ph_receipt")}</p>
      <p className="font-mono">{t("gross")}: {r.gross_kg} · {t("tare")}: {r.tare_kg} · <b data-testid="ph-net-value">{t("net")}: {kg(r.net_kg)}</b></p>
      {r.diff_kg != null && <p className={`font-mono text-sm font-bold ${r.diff_kg < 0 ? "text-red-700" : "text-slate-700"}`} data-testid="ph-diff-value">{t("ph_diff")}: {r.diff_kg > 0 ? "+" : ""}{r.diff_kg} kg</p>}
      <p className="text-xs text-slate-500">{r.weigh_count} {t("weigh_count")} · {r.by}</p>
    </section>
  );
};

export const PhPanel = ({ bin, setBin }) => {
  const { t } = useLang();
  const { run, busy } = useAct(setBin);
  const types = useMaster("bin_types");
  const autoTare = types.find((x) => x.tare_kg !== null && x.tare_kg !== undefined)?.tare_kg;
  const [gross, setGross] = useState("");
  const [tare, setTare] = useState("");
  const tareVal = autoTare ?? (tare === "" ? null : Number(tare));
  const net = gross && tareVal !== null ? Math.round((Number(gross) - tareVal) * 100) / 100 : null;
  if (bin.status !== "dispatched") return null;
  return (
    <section className="card p-4 space-y-3 border-2 border-teal-600" data-testid="ph-panel">
      <h2 className="font-bold text-lg">{t("ph_receipt")}</h2>
      {bin.dispatch && <p className="text-sm text-slate-600">{t("ph_guide")} {bin.dispatch.number} · {bin.dispatch.driver_name} · {bin.dispatch.truck_plate}</p>}
      <div className="grid grid-cols-2 gap-3">
        <Inp label={t("gross")} type="number" inputMode="decimal" step="0.1" value={gross} onChange={(e) => setGross(e.target.value)} testid="ph-gross-input" />
        {autoTare != null
          ? <div><span className="eyebrow">{t("tare")}</span><p className="field mt-1 flex items-center bg-slate-100 font-mono" data-testid="ph-tare-auto">{autoTare}</p></div>
          : <Inp label={t("tare")} type="number" inputMode="decimal" step="0.1" value={tare} onChange={(e) => setTare(e.target.value)} testid="ph-tare-input" />}
      </div>
      <p className="text-xs text-slate-600">{t("ph_optional")}{net !== null && <b className="ml-2 font-mono text-base text-slate-900" data-testid="ph-net-preview">{t("net")}: {kg(net)}</b>}</p>
      <button className="btn-primary w-full" disabled={busy || !gross || tareVal === null}
        onClick={async () => { if (await run(() => api.post(`/bins/${bin.id}/ph-receipt`, { gross_kg: Number(gross), tare_kg: autoTare != null ? null : tareVal }))) setGross(""); }}
        data-testid="ph-save-button"><Scale size={20} />{bin.ph_receipt ? t("reweigh") : t("ph_save")}</button>
    </section>
  );
};

export const DockPanel = ({ bin, setBin }) => {
  const { t } = useLang();
  const { run, busy } = useAct(setBin);
  const types = useMaster("bin_types");
  const autoTare = types.find((x) => x.tare_kg !== null && x.tare_kg !== undefined)?.tare_kg;
  const [gross, setGross] = useState("");
  const [tare, setTare] = useState("");
  const [reason, setReason] = useState("");
  const d = bin.dock;
  const tareVal = autoTare ?? (tare === "" ? null : Number(tare));
  const net = gross && tareVal !== null ? Math.round((Number(gross) - tareVal) * 100) / 100 : null;
  if (!["ready", "sent", "received", "discrepancy"].includes(bin.status)) return null;
  return (
    <section className="card p-4 space-y-3 border-2 border-slate-400" data-testid="dock-panel">
      <h2 className="font-bold text-lg">{t("dock")}</h2>
      <div className="grid grid-cols-2 gap-3">
        <Inp label={t("gross")} type="number" inputMode="decimal" step="0.1" value={gross} onChange={(e) => setGross(e.target.value)} testid="dock-gross-input" />
        {autoTare !== undefined && autoTare !== null
          ? <div><span className="eyebrow">{t("tare")}</span><p className="field mt-1 flex items-center bg-slate-100 font-mono" data-testid="dock-tare-auto">{autoTare}</p></div>
          : <Inp label={t("tare")} type="number" inputMode="decimal" step="0.1" value={tare} onChange={(e) => setTare(e.target.value)} testid="dock-tare-input" />}
      </div>
      <p className="text-xs text-slate-600">{autoTare != null ? t("tare_auto") : t("tare_manual_needed")}{net !== null && <b className="ml-2 font-mono text-base text-slate-900" data-testid="dock-net-preview">{t("net")}: {kg(net)}</b>}</p>
      <button className="btn-secondary w-full" disabled={busy || !gross || tareVal === null}
        onClick={async () => { if (await run(() => api.post(`/bins/${bin.id}/dock`, { gross_kg: Number(gross), tare_kg: autoTare != null ? null : tareVal }))) setGross(""); }}
        data-testid="dock-weigh-button">{d ? t("reweigh") : t("weigh")}</button>
      {d && ["received", "discrepancy"].includes(bin.status) && <button className="btn-primary w-full" disabled={busy} onClick={() => run(() => api.post(`/bins/${bin.id}/verify`))} data-testid="dock-verify-button"><BadgeCheck size={20} />{t("confirm_verify")}</button>}
      {bin.status !== "discrepancy" && <div className="flex gap-2 items-end">
        <div className="flex-1"><Inp label={t("reason")} value={reason} onChange={(e) => setReason(e.target.value)} testid="discrepancy-reason-input" /></div>
        <button className="btn-secondary !border-red-400 text-red-800" disabled={busy || reason.trim().length < 3} onClick={() => run(() => api.post(`/bins/${bin.id}/discrepancy`, { reason }))} data-testid="raise-discrepancy-button"><AlertTriangle size={18} />{t("raise_discrepancy")}</button>
      </div>}
    </section>
  );
};

export const CorrectionPanel = ({ bin, setBin }) => {
  const { t } = useLang();
  const { run, busy } = useAct(setBin);
  const sectors = useMaster("sectors").filter((s) => s.farm_id === bin.farm_id);
  const [f, setF] = useState({ gross_kg: "", sector_id: "", reason: "" });
  const body = () => ({ reason: f.reason, ...(f.gross_kg ? { gross_kg: Number(f.gross_kg) } : {}), ...(f.sector_id ? { sector_id: f.sector_id } : {}) });
  return (
    <section className="card p-4 space-y-3" data-testid="correction-panel">
      <h2 className="font-bold text-lg flex items-center gap-2"><Pencil size={18} />{t("correction")}</h2>
      <p className="text-xs text-slate-600">{t("correction_hint")}</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <Inp label={t("gross")} type="number" step="0.1" value={f.gross_kg} onChange={(e) => setF({ ...f, gross_kg: e.target.value })} testid="correction-gross-input" />
        <Sel label={t("sector")} value={f.sector_id} onChange={(v) => setF({ ...f, sector_id: v })} options={sectors} testid="correction-sector-select" />
      </div>
      <Inp label={t("reason")} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} testid="correction-reason-input" />
      <button className="btn-secondary w-full" disabled={busy || f.reason.trim().length < 3 || (!f.gross_kg && !f.sector_id)}
        onClick={async () => { if (await run(() => api.post(`/bins/${bin.id}/correct`, body()))) setF({ gross_kg: "", sector_id: "", reason: "" }); }} data-testid="apply-correction-button">{t("apply_correction")}</button>
    </section>
  );
};
